import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SpectralPlotter from './components/SpectralPlotter/SpectralPlotter';
import AnalyseTab from '../Analyse/Analyse';
import ExploreTab from './components/ExploreTab/ExploreTab';
import { UploaderHomePage } from '../Uploader/uploaderHomePage';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const SpectralLibraryHomePage = ({ tab }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Function to determine active tab from URL or props
  const getTabFromPath = (path) => {
    if (path.includes('/database')) return 'explore';
    if (path.includes('/analyse')) return 'analyse';
    if (path.includes('/upload')) return 'upload';
    return 'explore'; // Default tab
  };
  
  // Initialize state with the correct tab based on initial URL or prop
  const [activeTab, setActiveTab] = useState(() => {
    if (tab) return tab;
    return getTabFromPath(location.pathname);
  });
  
  const [showRightColumn, setShowRightColumn] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState('');
  const [selectedPolymer, setSelectedPolymer] = useState('');
  const [variableInputValue, setVariableInputValue] = useState('');

  // Key change: Add useEffect to update tab when URL changes
  // This ensures the component responds to navigation from the header
  useEffect(() => {
    const tabFromPath = getTabFromPath(location.pathname);
    setActiveTab(tabFromPath);
  }, [location.pathname]);
  
  // Update URL when tab changes through UI interaction
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    
    // Update URL to the clean path version
    let path;
    switch(newTab) {
      case 'explore':
        path = '/spectral-library/database';
        break;
      case 'analyse':
        path = '/spectral-library/analyse';
        break;
      case 'upload':
        path = '/spectral-library/upload';
        break;
      default:
        path = '/spectral-library';
    }
    
    navigate(path);
  };
  
  const styles = {
    container: {
      padding: '20px',
      minHeight: '100vh'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px'
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#4b5563',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      transition: 'color 0.2s ease',
      padding: '8px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '500',
      color: '#111827'
    },
    actions: {
      display: 'flex',
      gap: '12px'
    },
    importButton: {
      padding: '8px 16px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      backgroundColor: 'white',
      color: '#4b5563',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    newAnalysisButton: {
      padding: '8px 16px',
      borderRadius: '8px',
      border: 'none',
      backgroundColor: '#111827',
      color: 'white',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    navContainer: {
      position: 'relative',
      marginBottom: '24px'
    },
    navTabs: {
      display: 'flex',
      gap: '4px',
      position: 'relative'
    },
    navButton: (isActive) => ({
      position: 'relative',
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: '500',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      backgroundColor: isActive ? 'white' : 'transparent',
      color: isActive ? '#111827' : '#4b5563',
      boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
    }),
    activeIndicator: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '2px',
      backgroundColor: '#111827',
      borderRadius: '2px'
    },
    contentContainer: {
      backgroundColor: '#F1F5F9',
      borderRadius: '8px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    button: {
      padding: '8px 16px',
      borderRadius: '4px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    primaryButton: {
      backgroundColor: '#000',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: 'white',
      border: '1px solid #e0e0e0'
    },
  };

  const handlePlot = () => {
    if (selectedInstrument && selectedPolymer && variableInputValue) {
      setShowRightColumn(true);
      toast.info('Plotting plastic spectrum, please wait...', {
        position: "top-center", // Changed this line
        autoClose: 500,
        toastId: 'downloading',
        style: {
          color: '#282c34',
        },
        progressStyle: {
          background: '#282c34',
        },
      });
    } else {
      toast.error(`Please ensure you have filled out all available fields!`, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 5000,
      });
    }
  }; 
  
  const handleTabHover = (e) => {
    if (!e.target.classList.contains('active')) {
      e.target.style.backgroundColor = 'rgba(243, 244, 246, 0.5)';
      e.target.style.color = '#111827';
    }
  };

  const handleTabLeave = (e) => {
    if (!e.target.classList.contains('active')) {
      e.target.style.backgroundColor = 'transparent';
      e.target.style.color = '#4b5563';
    }
  };

  // Reset data when switching tabs to prevent stale data
  useEffect(() => {
    // Optionally reset form data when switching tabs
    setShowRightColumn(false);
    // You might want to decide whether to reset the form fields too
    // Uncomment these if you want a fresh start each time
    // setSelectedInstrument('');
    // setSelectedPolymer('');
    // setVariableInputValue('');
  }, [activeTab]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'explore':
        return (
          <ExploreTab 
            selectedInstrument={selectedInstrument}
            setSelectedInstrument={setSelectedInstrument}
            selectedPolymer={selectedPolymer}
            setSelectedPolymer={setSelectedPolymer}
            variableInputValue={variableInputValue}
            setVariableInputValue={setVariableInputValue}
            handlePlot={handlePlot}
            showRightColumn={showRightColumn}
            SpectralPlotter={SpectralPlotter}
          />
        );
      case 'analyse':
        return (
          <AnalyseTab />
        );
      case 'upload':
        return (
          <UploaderHomePage />
        );
      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.navContainer}>
        <div style={styles.navTabs}>
          {['explore', 'upload', 'analyse'].map(tab => (
            <button
              key={tab}
              style={styles.navButton(activeTab === tab)}
              onClick={() => handleTabChange(tab)}
              onMouseEnter={handleTabHover}
              onMouseLeave={handleTabLeave}
              className={activeTab === tab ? 'active' : ''}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {activeTab === tab && <div style={styles.activeIndicator} />}
            </button>
          ))}
        </div>
      </div>
      <div style={styles.contentContainer}>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default SpectralLibraryHomePage;